import React, {useEffect, useMemo, useState} from 'react';
import {NavLink, useSearchParams} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {
    Badge,
    Button,
    ButtonGroup,
    ListGroup,
    ListGroupItem,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
} from 'reactstrap';
import UserCard from '../../components/UserCard';
import FiltersLabel from '../../components/FiltersLabel';
import Loading from '../../components/Loading';
import {fetchUsers} from '../../slices/user.slice';
import {fetchOrganizations} from '../../slices/organizations.slice';
import {isAdmin} from '../../utils/admin';
import PageHeader from '../../components/PageHeader';
import SearchBar from '../../components/SearchBar';
import {imgUrl} from '../../utils/api-request';
import {roles} from '../../components/RoleBadge';
import ExportModal from '../../components/ExportModal';
import api from '../../api';

const Users = () => {
    const dispatch = useDispatch();
    const [searchParams, setSearchParams] = useSearchParams();
    const { list: users, status, pagination } = useSelector((state) => state.users);
    const { user } = useSelector(state => state.auth);
    const { list: organizations } = useSelector(state => state.organizations);

    const parseURLFilters = () => {
        const rolesParam = searchParams.get('roles');
        const organizationsParam = searchParams.get('organizations');
        const hasPinCodeParam = searchParams.get('hasPinCode');
        const has2FAParam = searchParams.get('has2FA');
        const contentRestrictionsParam = searchParams.get('contentRestrictions');
        const searchParam = searchParams.get('search');
        const pageParam = searchParams.get('page');

        return {
            roles: rolesParam ? rolesParam.split(',') : [],
            organizations: organizationsParam ? organizationsParam.split(',') : [],
            hasPinCode: hasPinCodeParam === 'true' ? true : hasPinCodeParam === 'false' ? false : undefined,
            has2FA: has2FAParam === 'true' ? true : has2FAParam === 'false' ? false : undefined,
            contentRestrictions: contentRestrictionsParam ? contentRestrictionsParam.split(',').map(Number) : [],
            search: searchParam || '',
            page: pageParam ? parseInt(pageParam, 10) : 1,
        };
    };

    const [filterBy, setFilterBy] = useState(parseURLFilters());
    const [searchValue, setSearchInput] = useState(parseURLFilters().search);
    const [searchOrganizationsValue, setSearchOrganizationsValue] = useState('');
    const [exportModal, setExportModal] = useState(false);
    const [filterModal, setFilterModal] = useState({ isOpen: false, filter: '' });

    const toggleFilterModal = filter => {
        setFilterModal({ isOpen: !filterModal.isOpen, filter });
    };

    const updateURLParams = (newFilters) => {
        const params = new URLSearchParams();

        if (newFilters.roles.length > 0) {
            params.set('roles', newFilters.roles.join(','));
        }
        if (newFilters.organizations.length > 0) {
            params.set('organizations', newFilters.organizations.join(','));
        }
        if (newFilters.hasPinCode !== undefined) {
            params.set('hasPinCode', newFilters.hasPinCode);
        }
        if (newFilters.has2FA !== undefined) {
            params.set('has2FA', newFilters.has2FA);
        }
        if (newFilters.contentRestrictions.length > 0) {
            params.set('contentRestrictions', newFilters.contentRestrictions.join(','));
        }
        if (newFilters.search) {
            params.set('search', newFilters.search);
        }
        if (newFilters.page && newFilters.page !== 1) {
            params.set('page', newFilters.page);
        }

        setSearchParams(params);
    };

    const toggleFilter = (filterKey, value) => {
        setFilterBy((prevFilterBy) => {
            const isSelected = prevFilterBy[filterKey].includes(value);
            const newFilters = {
                ...prevFilterBy,
                [filterKey]: isSelected
                    ? prevFilterBy[filterKey].filter((item) => item !== value)
                    : [...prevFilterBy[filterKey], value],
                page: 1,
            };
            updateURLParams(newFilters);
            return newFilters;
        });
    };

    const toggleBooleanFilter = (filterKey) => {
        setFilterBy((prevFilterBy) => {
            const currentValue = prevFilterBy[filterKey];
            let newValue;

            if (currentValue === undefined) {
                newValue = true;
            } else if (currentValue === true) {
                newValue = false;
            } else {
                newValue = undefined;
            }

            const newFilters = {
                ...prevFilterBy,
                [filterKey]: newValue,
                page: 1,
            };
            updateURLParams(newFilters);
            return newFilters;
        });
    };

    const handleSearch = e => {
        const query = e.target.value;
        setSearchInput(query);

        const newFilters = {
            ...filterBy,
            search: query,
            page: 1,
        };
        setFilterBy(newFilters);
        updateURLParams(newFilters);
    };

    const handleExport = async (fields, filetype = 'csv') => {
        try {
            const response = await api.get('/users', {
                params: { ...filterBy, export: filetype, fields },
                responseType: 'blob',
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `users.${filetype}`);
            document.body.appendChild(link);
            link.click();

            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading file:', error);
        }
    };

    const isLoading = ['idle', 'pending', 'loading'].includes(status);

    const areFiltersActive = useMemo(() => {
        const { roles, organizations, hasPinCode, has2FA, contentRestrictions } = filterBy;

        return (
            roles.length > 0 ||
            organizations.length > 0 ||
            hasPinCode !== undefined ||
            has2FA !== undefined ||
            contentRestrictions.length > 0
        );
    }, [filterBy]);

    const clearFilters = () => {
        const newFilters = {
            roles: [],
            organizations: [],
            hasPinCode: undefined,
            has2FA: undefined,
            contentRestrictions: [],
            search: '',
            page: 1,
        };
        setFilterBy(newFilters);
        setSearchInput('');
        updateURLParams(newFilters);
    };

    const handlePageChange = (newPage) => {
        const newFilters = {
            ...filterBy,
            page: newPage,
        };
        setFilterBy(newFilters);
        updateURLParams(newFilters);
    };

    const bacView = ['bac-admin', 'bac-user', 'root'].includes(user?.role);
    const contentRestrictionLevels = [6, 9, 12, 14, 16, 18];

    // Filter available roles based on user role
    const availableRoles = useMemo(() => {
        if (bacView) {
            return roles;
        } else if (user?.role === 'site-admin') {
            return roles.filter(r => ['site-admin', 'site-user', 'site-guest'].includes(r.role));
        } else if (user?.role === 'site-user') {
            return roles.filter(r => ['site-user', 'site-guest'].includes(r.role));
        } else {
            return roles.filter(r => r.role === 'site-guest');
        }
    }, [bacView, user?.role]);

    useEffect(() => {
        dispatch(fetchUsers(filterBy));
    }, [dispatch, filterBy]);

    useEffect(() => {
        if (bacView) {
            dispatch(fetchOrganizations());
        }
    }, [dispatch, bacView]);

    return (
        <div className='users-view page px-4 py-3 d-flex flex-column overflow-y-auto'>
            <PageHeader
                title='users'
                addButtonAction={isAdmin(user) ? {navigateTo: "/users/new"} : null}
                searchBar={{searchValue, handleSearch}}
            />

            <div className='contentContainer'>
                <div className="d-flex justify-content-start align-items-center gap-3">
                    <FiltersLabel active={areFiltersActive} onClear={clearFilters}/>

                    <ButtonGroup className="gap-2">
                        <Button color="primary" outline size="sm" className="rounded-3" onClick={() => toggleFilterModal('roles')}>
                            {filterBy.roles.length > 0 ? (
                                <Badge color="success" className="me-2 text-white">
                                    {filterBy.roles.length}
                                </Badge>
                            ) : (
                                <span className="fas fa-user-tag me-2" />
                            )}
                            Roles
                        </Button>

                        {bacView && (
                            <Button color="primary" outline size="sm" className="rounded-3" onClick={() => toggleFilterModal('organizations')}>
                                {filterBy.organizations.length > 0 ? (
                                    <Badge color="success" className="me-2 text-white">
                                        {filterBy.organizations.length}
                                    </Badge>
                                ) : (
                                    <span className="fas fa-building me-2"/>
                                )}
                                Organizations
                            </Button>
                        )}

                        <Button color="primary" outline size="sm" className="rounded-3" onClick={() => toggleFilterModal('contentRestrictions')}>
                            {filterBy.contentRestrictions.length > 0 ? (
                                <Badge color="success" className="me-2 text-white">
                                    {filterBy.contentRestrictions.length}
                                </Badge>
                            ) : (
                                <span className="fas fa-film me-2"/>
                            )}
                            Content Restrictions
                        </Button>

                        <Button
                            color="primary"
                            outline={filterBy.hasPinCode === undefined}
                            size="sm"
                            className="rounded-3"
                            onClick={() => toggleBooleanFilter('hasPinCode')}
                        >
                            {filterBy.hasPinCode === true ? (
                                <span className="fas fa-toggle-on me-2 text-success"/>
                            ) : filterBy.hasPinCode === false ? (
                                <span className="fas fa-toggle-off me-2 text-danger"/>
                            ) : (
                                <span className="fas fa-calculator me-2"/>
                            )}
                            PIN Code
                        </Button>

                        <Button
                            color="primary"
                            outline={filterBy.has2FA === undefined}
                            size="sm"
                            className="rounded-3"
                            onClick={() => toggleBooleanFilter('has2FA')}
                        >
                            {filterBy.has2FA === true ? (
                                <span className="fas fa-toggle-on me-2 text-success"/>
                            ) : filterBy.has2FA === false ? (
                                <span className="fas fa-toggle-off me-2 text-danger"/>
                            ) : (
                                <span className="fas fa-shield-alt me-2"/>
                            )}
                            2FA
                        </Button>
                    </ButtonGroup>

                    <div className="flex-grow-1">
                        <span className="fas fa-list me-2 text-primary" />
                        showing {pagination.totalCount} result{pagination.totalCount === 1 ? '' : 's'}
                    </div>

                    <div className="text-primary">
                        {bacView && (
                            <Button color="link" onClick={() => setExportModal(!exportModal)} className="text-decoration-none">
                                <span className="fas fa-file-excel me-1" /> Export
                            </Button>
                        )}
                    </div>
                </div>

                {isLoading && <Loading length={3} />}

                {status === 'success' && users.map(user => (
                    <NavLink to={`/users/${user._id}`} className="text-decoration-none" key={user._id}>
                        <UserCard user={user} />
                    </NavLink>
                ))}

                {pagination.totalPages > 1 && (
                    <div className="d-flex justify-content-center align-items-center gap-3 my-4">
                        <Button
                            color="primary"
                            outline
                            size="sm"
                            disabled={pagination.currentPage === 1}
                            onClick={() => handlePageChange(pagination.currentPage - 1)}
                        >
                            <span className="fas fa-chevron-left me-2"/>
                            Previous
                        </Button>

                        <span className="text-white">
                            Page {pagination.currentPage} of {pagination.totalPages}
                        </span>

                        <Button
                            color="primary"
                            outline
                            size="sm"
                            disabled={!pagination.hasMore}
                            onClick={() => handlePageChange(pagination.currentPage + 1)}
                        >
                            Next
                            <span className="fas fa-chevron-right ms-2"/>
                        </Button>
                    </div>
                )}

                <div className="clearfix py-4" />
            </div>

            <Modal isOpen={filterModal.isOpen} toggle={() => toggleFilterModal()} size="md" scrollable>
                <ModalHeader toggle={() => toggleFilterModal()}>
                    Filter by {filterModal.filter}
                </ModalHeader>
                <ModalBody>

                    {filterModal.filter === 'roles' && (
                        <div className="roles-filter-modal">
                            <ListGroup>
                                {availableRoles.map(({ role, label, color, icon }) => (
                                    <ListGroupItem
                                        onClick={() => toggleFilter('roles', role)}
                                        key={role}
                                        style={{ cursor: 'pointer', borderRadius: '0' }}
                                        active={filterBy.roles.includes(role)}
                                        className="border-0 border-start border-primary border-2 position-relative
                                            bg-broken text-white d-flex justify-content-start gap-3 align-items-center mb-2"
                                    >
                                        <Badge color={color} className={role.startsWith('site-') ? 'text-black' : 'text-white'}>
                                            <span className={`fas me-2 ${icon}`}/>
                                            {label}
                                        </Badge>
                                        {filterBy.roles.includes(role) && (
                                            <div className="position-absolute top-0 end-0 mt-2 me-3 small">
                                                Selected <span className="fas fa-check-circle text-success"/>
                                            </div>
                                        )}
                                    </ListGroupItem>
                                ))}
                            </ListGroup>
                        </div>
                    )}

                    {filterModal.filter === 'organizations' && (
                        <div className="organizations-filter-modal">
                            <SearchBar onChange={({ target }) => setSearchOrganizationsValue(target.value)} initialValue={searchOrganizationsValue} />
                            <ListGroup>
                                {organizations.map(({ _id, name, logo }) => name.toLowerCase().includes(searchOrganizationsValue.toLowerCase()) && (
                                    <ListGroupItem
                                        onClick={() => toggleFilter('organizations', _id)}
                                        key={_id}
                                        style={{ cursor: 'pointer', borderRadius: '0' }}
                                        active={filterBy.organizations.includes(_id)}
                                        className="border-0 border-start border-primary border-2 position-relative
                                            text-white d-flex justify-content-start gap-3 align-items-center mb-2"
                                    >
                                        <img src={imgUrl(logo?.imageUrl)} width={50} alt=""/>
                                        <div className="flex-grow-1">
                                            {name}
                                        </div>
                                        {filterBy.organizations.includes(_id) && (
                                            <div className="position-absolute top-0 end-0 mt-2 me-3 small">
                                                Selected <span className="fas fa-check-circle text-success"/>
                                            </div>
                                        )}
                                    </ListGroupItem>
                                ))}
                            </ListGroup>
                        </div>
                    )}

                    {filterModal.filter === 'contentRestrictions' && (
                        <div className="content-restrictions-filter-modal">
                            <ListGroup>
                                {contentRestrictionLevels.map((level) => (
                                    <ListGroupItem
                                        onClick={() => toggleFilter('contentRestrictions', level)}
                                        key={level}
                                        style={{ cursor: 'pointer', borderRadius: '0' }}
                                        active={filterBy.contentRestrictions.includes(level)}
                                        className="position-relative bg-broken border-0
                                            text-white d-flex justify-content-start gap-3 align-items-center mb-2"
                                    >
                                        <Badge color="secondary" className="text-white">
                                            <span className="fas fa-film me-2"/>
                                            {level}+
                                        </Badge>
                                        {filterBy.contentRestrictions.includes(level) && (
                                            <div className="position-absolute top-0 end-0 mt-2 me-3 small">
                                                Selected <span className="fas fa-check-circle text-success"/>
                                            </div>
                                        )}
                                    </ListGroupItem>
                                ))}
                            </ListGroup>
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={() => toggleFilterModal()}>
                        <span className="fas fa-check-circle me-2" />
                        Show results
                    </Button>
                </ModalFooter>
            </Modal>

            <ExportModal
                isOpen={exportModal}
                toggle={() => setExportModal(!exportModal)}
                onExport={handleExport}
                entityName="Users"
                fields={[
                    'firstName',
                    'lastName',
                    'username',
                    'email',
                    'role',
                    'hasPinCode',
                    'createdAt',
                    'updatedAt',
                ]}
                subFields={{
                    parentOrganization: ['name', 'country', 'creditAmount', 'tier', 'isYacht'],
                    twoFactorAuth: ['isEnabled', 'lastVerified'],
                }}
                defaultSelectedFields={[
                    'firstName',
                    'lastName',
                    'username',
                    'email',
                    'role',
                    'hasPinCode',
                    'parentOrganization.name',
                    'parentOrganization.country',
                    'twoFactorAuth.isEnabled',
                ]}
            />
        </div>
    );
};

export default Users;
